/**
 * ERGO 360 Analytics - 统一埋点工具
 *
 * 用于 Google Analytics 4 (GA4) 事件追踪
 * 使用 gtag 方式发送事件
 */

const Ergo360Analytics = {
  /**
   * 发送事件到 GA4
   * @param {string} eventName - 事件名称
   * @param {Object} params - 事件参数
   */
  track(eventName, params = {}) {
    // 添加 ergo_360_ 前缀，便于在 GA4 中筛选
    const prefixedEventName = `ergo_360_${eventName}`;

    // 添加通用参数
    const eventParams = {
      ...params,
      page_location: window.location.href,
      page_title: document.title
    };

    // 使用 gtag 发送事件到 GA4
    if (typeof gtag === 'function') {
      gtag('event', prefixedEventName, eventParams);
    } else {
      console.warn('[Ergo360Analytics] gtag not found, event not sent:', prefixedEventName);
    }
  },

  // ===== Quiz 漏斗事件 =====

  /**
   * Quiz 开始 - 用户选择第一题选项
   */
  quizStart() {
    this.track('quiz_start', {
      question: 1
    });
  },

  /**
   * Quiz 步骤完成
   * @param {number} step - 步骤号 (1-5)
   * @param {string} answer - 选项文本
   */
  quizStepComplete(step, answer) {
    this.track('quiz_step_complete', {
      step: step,
      answer: answer
    });
  },

  /**
   * Quiz 返回上一步
   * @param {number} fromStep - 从哪一步返回
   */
  quizAbandon(fromStep) {
    this.track('quiz_abandon', {
      from_step: fromStep
    });
  },

  /**
   * Quiz 跳过
   * @param {number} atStep - 在哪一步跳过
   */
  quizSkip(atStep) {
    this.track('quiz_skip', {
      at_step: atStep
    });
  },

  /**
   * Quiz 完成
   * @param {string} resultCurve - 结果曲线 (NECK/SPINE/HIPS/LEGS)
   * @param {number|null} height - 身高
   * @param {number|null} weight - 体重
   * @param {string} unit - 单位 (metric/imperial)
   */
  quizComplete(resultCurve, height, weight, unit) {
    this.track('quiz_complete', {
      result_curve: resultCurve?.toUpperCase() || '',
      height: height || null,
      weight: weight || null,
      unit: unit || 'imperial'
    });
  },

  /**
   * Quiz 邮箱提交成功
   * @param {string} resultCurve - 结果曲线
   */
  quizEmailSubmit(resultCurve) {
    this.track('quiz_email_submit', {
      result_curve: resultCurve?.toUpperCase() || ''
    });
  },

  // ===== 视频互动事件 =====

  /**
   * 视频播放
   * @param {string} section - 所属 section (video_scroll/101/kol)
   * @param {string} videoName - 视频名称
   */
  videoPlay(section, videoName) {
    this.track('video_play', {
      section: section,
      video_name: videoName || ''
    });
  },

  /**
   * 视频播放完成
   * @param {string} section - 所属 section
   * @param {string} videoName - 视频名称
   * @param {number} duration - 视频时长(秒)
   */
  videoComplete(section, videoName, duration) {
    this.track('video_complete', {
      section: section,
      video_name: videoName || '',
      duration: duration || 0
    });
  },

  /**
   * 视频弹窗打开
   * @param {string} source - 触发来源 (cursor/mobile_btn)
   */
  videoModalOpen(source) {
    this.track('video_modal_open', {
      source: source
    });
  },

  // ===== 产品交互事件 =====

  /**
   * 身体部位切换
   * @param {string} from - 原状态
   * @param {string} to - 新状态
   */
  bodyPartSwitch(from, to) {
    this.track('body_part_switch', {
      from: from,
      to: to
    });
  },

  /**
   * Hotspot 展开
   * @param {string} productName - 产品名称
   * @param {string} bodyPart - 身体部位
   */
  hotspotExpand(productName, bodyPart) {
    this.track('hotspot_expand', {
      product_name: productName,
      body_part: bodyPart
    });
  },

  /**
   * 产品弹窗打开
   * @param {string} productName - 产品名称
   */
  popupOpen(productName) {
    this.track('popup_open', {
      product_name: productName
    });
  },

  /**
   * 弹窗内点击"查看产品"
   * @param {string} productName - 产品名称
   */
  popupViewProduct(productName) {
    this.track('popup_view_product', {
      product_name: productName
    });
  },

  // ===== Bundle 转化事件 =====

  /**
   * Bundle Tab 切换
   * @param {string} tab - Tab 名称 (Basic/Plus/Premium)
   */
  bundleTabSwitch(tab) {
    this.track('bundle_tab_switch', {
      tab: tab
    });
  },

  /**
   * Bundle 产品勾选/取消
   * @param {string} productName - 产品名称
   * @param {string} action - 动作 (select/deselect)
   * @param {string} tab - 当前 Tab
   */
  bundleProductToggle(productName, action, tab) {
    this.track('bundle_product_toggle', {
      product_name: productName,
      action: action,
      tab: tab
    });
  },

  /**
   * Bundle 加入购物车
   * @param {string} tab - 当前 Tab
   * @param {Array} products - 产品列表
   * @param {number} totalPrice - 总价(分)
   */
  bundleAddToCart(tab, products, totalPrice) {
    this.track('bundle_add_to_cart', {
      tab: tab,
      products: products,
      total_price: totalPrice,
      currency: 'USD'
    });
  },

  // ===== CTA 事件 =====

  /**
   * CTA 按钮点击
   * @param {string} buttonText - 按钮文本
   * @param {string} destination - 目标链接
   */
  ctaClick(buttonText, destination) {
    this.track('cta_click', {
      button_text: buttonText,
      destination: destination
    });
  },

  // ===== 101 轮播事件 =====

  /**
   * 轮播导航切换
   * @param {string} direction - 方向 (prev/next)
   * @param {number} fromSlide - 原幻灯片索引
   * @param {number} toSlide - 目标幻灯片索引
   */
  carouselNavigate(direction, fromSlide, toSlide) {
    this.track('carousel_navigate', {
      direction: direction,
      from_slide: fromSlide,
      to_slide: toSlide
    });
  },

  /**
   * 轮播视频播放
   * @param {number} slideIndex - 幻灯片索引
   * @param {string} videoTitle - 视频标题
   */
  carouselVideoPlay(slideIndex, videoTitle) {
    this.track('carousel_video_play', {
      slide_index: slideIndex,
      video_title: videoTitle || ''
    });
  },

  // ===== KOL 事件 =====

  /**
   * KOL 视频播放
   * @param {string} kolUsername - KOL 用户名
   */
  kolVideoPlay(kolUsername) {
    this.track('kol_video_play', {
      kol_username: kolUsername
    });
  },

  /**
   * KOL 卡片展开
   * @param {string} kolUsername - KOL 用户名
   */
  kolCardExpand(kolUsername) {
    this.track('kol_card_expand', {
      kol_username: kolUsername
    });
  }
};

// 导出到全局
window.Ergo360Analytics = Ergo360Analytics;
